import React, { useState } from "react";
import { useGetAdminWorkflowExecutionsQuery } from "@/store/admin_api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  COMPLETED: "default",
  RUNNING: "secondary",
  FAILED: "destructive",
  CANCELLED: "outline",
};

export function WorkflowRunsTab() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { data: executions, isLoading } = useGetAdminWorkflowExecutionsQuery({ page, size: 50 });

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflow Runs</CardTitle>
        <CardDescription>Every workflow execution across all organisations, newest first.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!executions || executions.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No workflow runs yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Triggered</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Workflow</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {executions.items.map((execution) => (
                <TableRow
                  key={execution.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/platform-admin/workflow-runs/${execution.id}`)}
                >
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {execution.triggeredAt ? format(new Date(execution.triggeredAt), "MMM dd, yyyy HH:mm") : "—"}
                  </TableCell>
                  <TableCell className="font-medium">{execution.workflow?.organisation?.name ?? "—"}</TableCell>
                  <TableCell>{execution.workflow?.name ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{execution.workflow?.triggerType ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANTS[execution.status] ?? "outline"}>{execution.status}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground">{execution.error}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {executions && executions.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {executions.page + 1} of {executions.totalPages}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={executions.last} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
